import AudioVisualizer from "./AudioVisualizer";
import RecordingButton from "./RecordingButton";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { pollStatus } from "../../pronunciation";
import { useTranslation } from "react-i18next";

const RecState = ({
    currentlyRecording,
    setCurrentlyRecording,
    recordingState,
    setRecordingState,
    text,
    selectedLanguage,
    setTextareaHasError,
    visualizerMaxWidth = 300,
    stackResults = false,
    onResult,
}) => {
    const { t } = useTranslation();

    const canvasRef = useRef(null);
    const drawingWidthRef = useRef(0);
    const [canvasImage, setCanvasImage] = useState(null);
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
    const [celeryTaskId, setCeleryTaskId] = useState(null);
    const [audioDuration, setAudioDuration] = useState(null);
    const [pixelsPerSecond, setPixelsPerSecond] = useState(null);
    const [canvasForColoring, setCanvasForColoring] = useState(null);
    const [recordingBlob, setRecordingBlob] = useState(null);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [playheadX, setPlayheadX] = useState(0);
    const rafRef = useRef(null);
    const [understoodText, setUnderstoodText] = useState("");
    const [scores, setScores] = useState(null);
    const [shareCode, setShareCode] = useState("");

    // Add refs to track processing state
    const isProcessingRef = useRef(false);
    const intervalRef = useRef(null);

    // Keep the latest onResult + text in refs so the polling effect never
    // restarts (or goes stale) when the parent passes a fresh callback identity.
    const onResultRef = useRef(onResult);
    const textRef = useRef(text);
    useEffect(() => {
        onResultRef.current = onResult;
        textRef.current = text;
    }, [onResult, text]);

    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            setContainerWidth(containerRef.current.offsetWidth);
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    const audioUrl = useMemo(() => {
        if (!recordingBlob) return null;
        return URL.createObjectURL(recordingBlob);
    }, [recordingBlob]);

    useEffect(() => {
        return () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        // force reload the new src before attempting play
        el.load();
        setIsPlaying(false);
    }, [audioUrl]);

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    const togglePlay = async () => {
        const el = audioRef.current;
        if (!el) return;

        if (isPlaying) {
            el.pause();
            return;
        }

        // If metadata isn't ready yet, wait for it once
        if (el.readyState < 1) {
            await new Promise((resolve) => {
                const once = () => {
                    el.removeEventListener("loadedmetadata", once);
                    resolve();
                };
                el.addEventListener("loadedmetadata", once, { once: true });
            });
        }

        try {
            await el.play(); // will reject on policy/MIME errors
        } catch (err) {
            console.error("Audio play() failed:", err);
            // Helpful UI if you want: toast the error message here
        }
    };

    useEffect(() => {
        let rafId;
        const el = audioRef.current;
        if (!el) return;

        const update = () => {
            if (!audioDuration || !containerWidth) {
                rafId = requestAnimationFrame(update);
                return;
            }
            const ratio = (el.currentTime * 1000) / audioDuration; // ms vs s
            const x = ratio * containerWidth;
            setPlayheadX(x);
            rafId = requestAnimationFrame(update);
        };

        if (isPlaying) {
            rafId = requestAnimationFrame(update);
        }

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isPlaying, audioDuration, containerWidth]);

    // When transitioning to result state, capture the canvas as an image
    useEffect(() => {
        if (recordingState === "analyzing" && canvasRef.current) {
            // Define generateCanvasImage inside useEffect
            const generateCanvasImage = () => {
                // copy over everything from the Canvas, but trim it to
                // its actual size
                const tempCanvas = document.createElement("canvas");
                const tempCtx = tempCanvas.getContext("2d");
                const actualWidth = drawingWidthRef.current;
                tempCanvas.width = actualWidth;
                tempCanvas.height = canvasRef.current.height;
                setCanvasForColoring(tempCanvas);
                setPixelsPerSecond(tempCanvas.width / audioDuration);
                tempCtx.fillRect(0, 0, actualWidth, tempCanvas.height);
                tempCtx.drawImage(
                    canvasRef.current,
                    0,
                    0,
                    actualWidth,
                    canvasRef.current.height,
                    0,
                    0,
                    actualWidth,
                    tempCanvas.height,
                );

                const recordingDataURL = tempCanvas.toDataURL("image/png");
                setCanvasImage(recordingDataURL);
            };

            // Get the image data from the offscreen canvas
            generateCanvasImage();

            // Store the original dimensions
            setCanvasDimensions({
                width: drawingWidthRef.current,
                height: canvasRef.current.height,
            });
        }
    }, [recordingState, audioDuration]);

    useEffect(() => {
        // Reset processing flags when celeryTaskId changes or component unmounts
        return () => {
            isProcessingRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [celeryTaskId]);

    useEffect(() => {
        if (!celeryTaskId) return;

        // Reset flags for new task
        isProcessingRef.current = false;

        let isMounted = true; // Add this flag

        // Define colorCanvas inside useEffect
        const colorCanvas = (offsets) => {
            // Prevent multiple coloring operations
            if (!canvasForColoring || !pixelsPerSecond || isProcessingRef.current || !isMounted) {
                return;
            }

            // Mark as processing to prevent duplicate calls
            isProcessingRef.current = true;

            const ctx = canvasForColoring.getContext("2d");

            // Draw a clean copy of the original waveform first
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = canvasForColoring.width;
            tempCanvas.height = canvasForColoring.height;
            tempCanvas.getContext("2d").drawImage(canvasForColoring, 0, 0);

            // Apply colors on top of the waveform
            offsets.forEach((offset) => {
                const percentage = offset[2];
                if (percentage < 60) {
                    ctx.fillStyle = "rgba(255, 0, 0, 0.5)";
                } else if (percentage < 87) {
                    ctx.fillStyle = "rgba(255, 255, 0, 0.5)";
                } else {
                    ctx.fillStyle = "rgba(0, 255, 0, 0.5)";
                }

                ctx.fillRect(
                    offset[0] * pixelsPerSecond,
                    0,
                    offset[1] * pixelsPerSecond,
                    canvasForColoring.height,
                );
            });

            // Update the image display
            setCanvasImage(canvasForColoring.toDataURL("image/png"));
        };

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(async () => {
            try {
                const status = await pollStatus(celeryTaskId);

                // Only update state if component is still mounted
                if (!isMounted) return;

                // if task is finished, clear the interval and advance state
                if (status.status === "SUCCESS") {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    // Only proceed if not already processed
                    if (!isProcessingRef.current) {
                        setRecordingState("result");
                        colorCanvas(status.result[1]);
                        setUnderstoodText(status.result[2]);
                        setScores(status.result[0].Paragraph);
                        setShareCode(status.result[3]);
                        // Surface the result so a parent (e.g. the public course
                        // page) can record the score + share_code for this session.
                        onResultRef.current?.({
                            sentence: textRef.current,
                            shareCode: status.result[3],
                            scores: status.result[0].Paragraph,
                        });
                    }
                } else if (status.status === "FAILURE") {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    alert(t("alert.analysis-failed"));
                    resetEverthing();
                    setRecordingState("idle");
                }
            } catch (error) {
                console.error("Failed to fetch status", error);
                if (isMounted && intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        }, 500);

        return () => {
            isMounted = false; // Mark as unmounted
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [celeryTaskId, audioDuration, canvasForColoring, pixelsPerSecond, setRecordingState]);

    // Calculate appropriate height based on aspect ratio and max width
    const getScaledHeight = () => {
        if (!canvasDimensions.width || !canvasDimensions.height) return "auto";

        const aspectRatio = canvasDimensions.width / canvasDimensions.height;
        const maxDisplayWidth = Math.min(800, window.innerWidth - 40); // Account for margins
        return `${maxDisplayWidth / aspectRatio}px`;
    };

    const tick = useCallback(() => {
        const audio = audioRef.current;
        const container = containerRef.current;
        if (!audio || !container || !audioDuration) return;

        // audio.currentTime is seconds; audioDuration is ms

        const progress = Math.min(
            (audio.currentTime * 1000) / Math.min(audioDuration, audio.duration * 1000 + 5),
            1,
        );
        const x = progress * container.clientWidth;

        const el = container.querySelector("[data-playhead]");
        if (el) el.style.transform = `translateX(${x}px)`;

        if (!audio.paused && !audio.ended) {
            rafRef.current = requestAnimationFrame(tick);
        }
    }, [audioDuration]);

    // Start/stop rAF on audio events
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlay = () => {
            setIsPlaying(true);
            rafRef.current = requestAnimationFrame(tick);
        };
        const onPause = () => {
            setIsPlaying(false);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        const onEnded = () => {
            setIsPlaying(false);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };

        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("ended", onEnded);

        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("ended", onEnded);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [tick]);

    const resetEverthing = () => {
        setCeleryTaskId(null);
        setIsPlaying(false);
        setPlayheadX(0);
        setUnderstoodText("");
        setScores(null);
        isProcessingRef.current = false;
    };

    const handleShareClick = async () => {
        console.log("share click");
        const shareUrl = `${window.location.origin}/share/${shareCode}`;
        const title = t("share-metadata.title");
        const text = t("share-metadata.text");
        try {
            // Prefer native browser share if available (requires HTTPS + user gesture)
            if (navigator.share) {
                await navigator.share({ title, text, url: shareUrl });
                return;
            }
        } catch (err) {
            // User canceled
        }
    };

    const handleCanvasClick = (e) => {
        const container = containerRef.current;
        const audio = audioRef.current;
        if (!container || !audio || !audioDuration) return;

        // Get the click position relative to the container
        const rect = container.getBoundingClientRect();
        const clickX = e.clientX - rect.left;

        // Calculate the ratio of click position to container width
        const ratio = Math.max(0, Math.min(1, clickX / container.clientWidth));

        // Convert to audio time (audioDuration is in ms, audio.currentTime is in seconds)
        const newTime = (ratio * audioDuration) / 1000;

        // Update audio position
        audio.currentTime = newTime;

        // Update playhead position
        setPlayheadX(clickX);
    };

    switch (recordingState) {
        case "idle":
            return (
                <div className="flex items-center justify-center space-x-4">
                    <RecordingButton
                        currentlyRecording={currentlyRecording}
                        setCurrentlyRecording={setCurrentlyRecording}
                        setRecordingState={setRecordingState}
                        text={text}
                        selectedLanguage={selectedLanguage}
                        setCeleryTaskId={setCeleryTaskId}
                        setAudioDuration={setAudioDuration}
                        setRecordingBlob={setRecordingBlob}
                        setTextareaHasError={setTextareaHasError}
                    />
                </div>
            );
        case "recording":
            return (
                <div className="flex items-center justify-center space-x-4">
                    <RecordingButton
                        currentlyRecording={currentlyRecording}
                        setCurrentlyRecording={setCurrentlyRecording}
                        setRecordingState={setRecordingState}
                        text={text}
                        selectedLanguage={selectedLanguage}
                        setCeleryTaskId={setCeleryTaskId}
                        setAudioDuration={setAudioDuration}
                        setRecordingBlob={setRecordingBlob}
                    />
                    <div className="absolute -translate-x-[calc(50%+4.5rem)] md:-translate-x-[calc(50%+5rem-1px)] self-start">
                        <AudioVisualizer
                            externalOffscreenCanvasRef={canvasRef}
                            drawingWidthRef={drawingWidthRef}
                            maxWidth={visualizerMaxWidth}
                        />
                    </div>
                </div>
            );

        case "analyzing":
        case "result":
            return (
                <div className="flex flex-col items-center space-y-4 w-full md:px-4">
                    {recordingBlob && (
                        <audio
                            ref={audioRef}
                            src={audioUrl ?? undefined}
                            onPlay={onPlay}
                            onPause={onPause}
                            onEnded={onEnded}
                            preload="metadata"
                            className="hidden"
                        />
                    )}
                    {canvasImage ? (
                        <div className={stackResults ? "w-full" : undefined}>
                            {understoodText ? <p>{t("what-you-said")}</p> : null}
                            <div
                                className={`flex flex-col ${stackResults ? "" : "sm:flex-row"} gap-8`}
                            >
                                <div
                                    className={`w-full ${stackResults ? "" : "max-w-lg"} min-h-36 h-36 rounded-xl bg-secondary border border-border overflow-hidden`}
                                >
                                    {understoodText && scores ? (
                                        <div className="flex flex-col h-full">
                                            <p className="flex-1 p-4 font-medium">
                                                {understoodText}
                                            </p>
                                            <div className="flex h-8 text-xs font-medium text-white gap-px bg-purple-600">
                                                <div className="bg-primary flex-1 flex items-center justify-center">
                                                    {t("scores.accuracy") +
                                                        ": " +
                                                        Math.ceil(scores.accuracy_score)}
                                                </div>
                                                <div className="bg-primary flex-1 flex items-center justify-center">
                                                    {t("scores.completeness") +
                                                        ": " +
                                                        Math.ceil(scores.completeness_score)}
                                                </div>
                                                <div className="bg-primary flex-1 flex items-center justify-center">
                                                    {t("scores.fluency") +
                                                        ": " +
                                                        Math.ceil(scores.fluency_score)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-4 items-center justify-center h-full">
                                            <div role="status">
                                                <svg
                                                    aria-hidden="true"
                                                    className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary"
                                                    viewBox="0 0 100 101"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                        fill="currentColor"
                                                    />
                                                    <path
                                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                        fill="currentFill"
                                                    />
                                                </svg>
                                                <span className="sr-only">
                                                    {t("analysis-loading") /* screen reader */}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm italic text-gray-600 px-4 text-center">
                                                    {t("tooltip-loading-analysis")}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center">
                                    <button
                                        className="p-px text-white rounded-full bg-primary hover:bg-purple-500"
                                        onClick={togglePlay}
                                    >
                                        {isPlaying ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="size-5"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="size-5"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                    <div
                                        className="max-w-full overflow-x-auto relative cursor-pointer"
                                        ref={containerRef}
                                        onClick={handleCanvasClick}
                                    >
                                        <img
                                            src={canvasImage}
                                            alt="Audio visualization"
                                            className="rounded-lg"
                                            style={{
                                                maxWidth: "100%",
                                                width: "100%",
                                                maxHeight: getScaledHeight(),
                                                objectFit: "contain",
                                            }}
                                        />
                                        <div
                                            data-playhead
                                            className="top-0 absolute w-px h-full bg-red-500 pointer-events-none"
                                            style={{
                                                transform: `translateX(${playheadX}px)`,
                                                willChange: "transform",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            {scores ? (
                                <div className="flex justify-start gap-2 w-full mt-4">
                                    <button
                                        className="p-2 border border-border rounded-md hover:bg-gray-100"
                                        onClick={handleShareClick}
                                    >
                                        <div className="flex flex-row gap-1 items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth={2}
                                                stroke="currentColor"
                                                className="size-4"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                                                />
                                            </svg>

                                            <p className="text-sm">{t("share-result")}</p>
                                        </div>
                                    </button>

                                    <button
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-purple-600"
                                        onClick={() => {
                                            resetEverthing();
                                            setRecordingState("idle");
                                        }}
                                    >
                                        <div className="flex flex-row gap-1 items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth={2}
                                                stroke="currentColor"
                                                className="size-4"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                                />
                                            </svg>

                                            <p className="text-sm">{t("record-again")}</p>
                                        </div>
                                    </button>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    ) : (
                        <div className="p-4 bg-gray-100 rounded-lg">
                            No audio visualization available
                        </div>
                    )}
                </div>
            );

        default:
            return <p>Default</p>;
    }
};

export default RecState;
